// Evidence status — the promotion bar, as data.
//
// Intent decision 4 (docs/intent/001-diagnostic-redesign/intent.md): a dimension
// moves from "preference" to "measured" only on BOTH of:
//   (a) stability  — Cronbach's alpha >= .70 and acceptable test-retest, beta wave 1
//   (b) benefit    — an A/B in wave 2 showing that adapting on the dimension
//                    improves checkpoint performance against control
//
// Either one alone leaves the dimension at `preference`. Neither can be met before
// launch, so at launch every self-report dimension is a PREFERENCE and the product
// must not describe it as a measurement. This registry is what the UI reads for its
// verb, so the claim and the evidence cannot drift apart.

export type EvidenceStatus = 'preference' | 'measured';

export interface DimensionEvidence {
  status: EvidenceStatus;
  /** How the score is obtained — the honest one-liner behind the UI's verb. */
  basis: string;
  /** What remains before this dimension could be promoted. Empty once `measured`. */
  outstanding: string[];
}

/** Bump when a dimension is promoted, so profiles record which registry rendered them. */
export const EVIDENCE_STATUS_VERSION = 'evidence-v1-2026-09-05';

const SELF_REPORT_OUTSTANDING = [
  'reliability: Cronbach alpha >= .70 and acceptable test-retest on beta wave 1',
  'outcome validity: wave-2 A/B showing adapting on this dimension improves checkpoint performance',
];

export const DIMENSION_EVIDENCE: Record<string, DimensionEvidence> = {
  contentFormat:    { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  learningApproach: { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  depthTolerance:   { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  sequencing:       { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  infoDensity:      { status: 'preference', basis: 'self-report plus one timed comprehension task', outstanding: SELF_REPORT_OUTSTANDING },
  motivation:       { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  viewpointRange:   { status: 'preference', basis: 'self-report', outstanding: SELF_REPORT_OUTSTANDING },
  // The two things the product may honestly call measured: both are behavioural,
  // neither is a self-report.
  priorKnowledge:   { status: 'measured', basis: 'assessed per upload against the material itself', outstanding: [] },
  checkpointPerformance: { status: 'measured', basis: 'scored checkpoint answers', outstanding: [] },
};

export function evidenceStatusFor(dimensionKey: string): EvidenceStatus {
  return DIMENSION_EVIDENCE[dimensionKey]?.status ?? 'preference';
}

/**
 * The verb the UI is allowed to use for a dimension. Nothing may say "measured"
 * until the registry says so.
 */
export function evidenceVerb(dimensionKey: string): string {
  return evidenceStatusFor(dimensionKey) === 'measured'
    ? "what we've observed as you learn"
    : 'how you told us you like to learn';
}
